import { LeadCard } from "./LeadCard";
import { Link } from "react-router-dom";
import LeadShimmerEffect from "./LeadShimmerEffect";
import { encodeURIComponent } from "@lib/utils";
import { useDynamicHeight } from "@customHooks/useDynamicHeight";
import { useIsMobile } from "@customHooks/use-mobile";
import { ListShimmer } from "./LeadCardShimmer";
import { LeadV2, PaginationMetadata } from "../services/leadsServiceV2";
import { Button } from "@components/shadcn-ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface LeadsListV2Props {
  leads: LeadV2[];
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  canGoBack: boolean;
  canGoNext: boolean;
  searchQuery?: string;
}

export const LeadsListV2Component = ({
  leads,
  pagination,
  isLoading,
  onNextPage,
  onPreviousPage,
  canGoBack,
  canGoNext,
  searchQuery = "",
}: LeadsListV2Props) => {
  const { ref, height } = useDynamicHeight();
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div className="mt-3">
        {isMobile ? (
          <div>
            <ListShimmer />
          </div>
        ) : (
          <LeadShimmerEffect />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 flex-1 mt-3">
      {/* Pagination Info */}
      {pagination && (
        <div className="flex justify-between items-center mb-3 px-2">
          <div className="text-sm text-muted-foreground">
            Showing {pagination.returnedRecords} results
            {pagination.currentPage > 1 && ` (Page ${pagination.currentPage})`}
          </div>
          
          {/* Pagination Controls */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviousPage}
              disabled={!canGoBack || isLoading}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={!canGoNext || isLoading}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Leads List */}
      <div
        className="flex flex-col gap-1 flex-1 overflow-auto"
        ref={ref}
        style={{
          maxHeight: `${height - 60}px`, // Account for pagination controls
        }}
      >
        {leads.length ? (
          leads.map(
            ({ id, firstName, lastName, phoneNumber, email, source }, index) => (
              <Link
                to={`/dashboard/lead-details/${encodeURIComponent(phoneNumber)}`}
                key={id || index}
              >
                <LeadCard
                  firstName={firstName}
                  lastName={lastName}
                  mobileNumber={phoneNumber}
                  emailAddress={email}
                  index={index}
                  source={source}
                />
              </Link>
            )
          )
        ) : (
          <p className="text-center text-gray-500 p-10">
            {searchQuery 
              ? `No leads found for the search query "${searchQuery}".`
              : "No leads found. Try adjusting your filters."
            }
          </p>
        )}
      </div>

      {/* Bottom Pagination (for mobile) */}
      {pagination && isMobile && (
        <div className="flex justify-center gap-2 mt-3 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onPreviousPage}
            disabled={!canGoBack || isLoading}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          
          <span className="flex items-center px-3 text-sm text-muted-foreground">
            Page {pagination.currentPage}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!canGoNext || isLoading}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
